import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Send, Clock, Users, Loader2, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const inputCls = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent outline-none bg-white';

const buildDefaultMessage = (req) => {
  const crew = (req.assigned_crew || []).map((c) => c.name).filter(Boolean).join(', ');
  const dateLine = req.end_date && req.end_date !== req.start_date
    ? `${req.start_date} to ${req.end_date}`
    : req.start_date;
  const tools = Array.isArray(req.tools) && req.tools.length > 0
    ? req.tools.map((t) => (t.qty > 1 ? `${t.qty}× ${t.label}` : t.label)).join(', ')
    : null;
  return [
    `🔧 WORK DISPATCH${req.customer_name ? ` - ${req.customer_name}` : ''}`,
    `📍 Location: ${req.job_site || 'TBD'}`,
    `📞 Site Contact: ${[req.contact, req.contact_phone].filter(Boolean).join(' — ') || 'TBD'}`,
    `🔖 Work Order: ${req.work_order_number || 'TBD'}`,
    `📅 Date: ${dateLine}`,
    `⏰ Time: ${[req.start_time, req.end_time].filter(Boolean).join(' - ') || 'TBD'}`,
    crew ? `Crew: ${crew}` : null,
    tools ? `🛠️ Tools: ${tools}` : null,
    '',
    'Tap ✓ Confirm or ✗ Can\'t make it in the job chat.',
  ].filter((l) => l !== null).join('\n');
};

// Default scheduling suggestion: the job's start date at 6:00 AM local
const defaultSendAt = (req) => `${req.start_date}T06:00`;

const DispatchSmsModal = ({ req, onSkip, onDone, restrictTo = null, pickRecipients = false }) => {
  const [message, setMessage] = useState(() => buildDefaultMessage(req));
  const [mode, setMode] = useState('now'); // 'now' | 'schedule'
  const [sendAt, setSendAt] = useState(() => defaultSendAt(req));
  const [busy, setBusy] = useState(false);
  // pickRecipients: checkbox per crew member — default-check anyone never texted yet
  const [checked, setChecked] = useState(() => {
    if (!pickRecipients) return {};
    const texted = new Set((req.dispatch_recipients || []).map((r) => r.crew_member_id));
    const map = {};
    (req.assigned_crew || []).forEach((c) => { map[c.id] = !texted.has(c.id); });
    return map;
  });

  const recipients = useMemo(() => {
    if (restrictTo?.length) {
      const crew = restrictTo.map((c) => ({ name: c.name, tag: 'New Crew' }));
      crew.push({ name: 'Dispatcher(s)', tag: 'Dispatch · in chat' });
      return crew;
    }
    const crew = (req.assigned_crew || []).map((c) => ({ name: c.name, tag: 'Crew' }));
    if (req.created_by_name) crew.push({ name: req.created_by_name, tag: 'PM · in chat' });
    crew.push({ name: 'Dispatcher(s)', tag: 'Dispatch · in chat' });
    return crew;
  }, [req, restrictTo]);

  const checkedIds = Object.keys(checked).filter((id) => checked[id]);

  const submit = async () => {
    if (!message.trim()) { toast.error('A dispatch message is required'); return; }
    if (pickRecipients && checkedIds.length === 0) { toast.error('Pick at least one crew member'); return; }
    if (mode === 'schedule' && !sendAt) { toast.error('Pick a date & time to schedule the dispatch'); return; }
    setBusy(true);
    try {
      const payload = mode === 'schedule'
        ? { mode: 'schedule', message, send_at: sendAt }
        : { mode: 'now', message };
      if (restrictTo?.length) payload.only_ids = restrictTo.map((c) => c.id);
      if (pickRecipients) payload.only_ids = checkedIds;
      const res = await axios.post(`${API}/dispatch/requests/${req.id}/dispatch-sms`, payload, authHeader());
      if (res.data?.status === 'scheduled') {
        toast.success(`Dispatch scheduled to post at ${new Date(sendAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
      } else {
        const sent = res.data?.recipients ?? recipients.length;
        const failed = res.data?.failed ?? 0;
        if (failed > 0) {
          const reasons = (res.data?.errors || []).join(' · ');
          toast.warning(`Dispatch posted for ${sent}, ${failed} failed${reasons ? ` — ${reasons}` : ''}`, { duration: 12000 });
        } else {
          toast.success(`Dispatch posted to the job chat — ${sent} people notified`);
        }
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post the dispatch');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="dispatch-sms-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="bg-green-600 p-2 rounded-lg"><MessageSquare className="text-white" size={18} /></div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{pickRecipients ? 'Post / re-post the dispatch' : restrictTo?.length ? 'Dispatch the new crew?' : 'Dispatch the crew?'}</h2>
              <p className="text-xs text-gray-500">{pickRecipients ? `${req.request_number} — pick who to ping. Anyone not dispatched yet is pre-selected.` : restrictTo?.length ? `${req.request_number} crew changed — dispatch only the newly added member${restrictTo.length > 1 ? 's' : ''}.` : `${req.request_number} approved — post the dispatch to the job chat now (push-notified) or schedule it.`}</p>
            </div>
          </div>
          <button onClick={onSkip} className="text-slate-500 hover:text-gray-700" data-testid="dispatch-sms-close"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Recipients */}
          <div>
            <label className="block text-xs font-semibold text-[#1e3a5f] mb-1.5 uppercase tracking-wide">Posting to</label>
            {pickRecipients ? (
              <div className="space-y-1" data-testid="dispatch-sms-recipient-picker">
                {(req.assigned_crew || []).map((c) => {
                  const rec = (req.dispatch_recipients || []).find((r) => r.crew_member_id === c.id);
                  const status = rec?.status;
                  return (
                    <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 cursor-pointer text-sm">
                      <input type="checkbox" checked={!!checked[c.id]}
                        onChange={(e) => setChecked((p) => ({ ...p, [c.id]: e.target.checked }))}
                        data-testid={`dispatch-sms-pick-${c.id}`} />
                      <span className="font-medium text-gray-800">{c.name}</span>
                      <span className={`ml-auto text-[11px] ${!rec ? 'text-amber-600 font-semibold' : status === 'confirmed' ? 'text-green-600' : status === 'declined' ? 'text-red-600' : 'text-slate-500'}`}>
                        {!rec ? 'Not dispatched yet' : status === 'confirmed' ? 'Confirmed ✓' : status === 'declined' ? 'Declined ✗' : 'Dispatched — awaiting ✓/✗'}
                      </span>
                    </label>
                  );
                })}
                <p className="text-[11px] text-slate-500 mt-1.5">Only checked crew get pinged (dispatchers see it in the chat). Re-dispatching someone resets their ✓/✗ status.</p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-1.5" data-testid="dispatch-sms-recipients">
                  {recipients.length === 0 ? (
                    <span className="text-sm text-amber-600">No crew or PM assigned.</span>
                  ) : recipients.map((r, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-[#eef3f9] text-[#1e3a5f] text-xs font-medium px-2 py-1 rounded-md border border-[#d6e2f0]">
                      <Users size={11} />{r.name}<span className="text-slate-500">· {r.tag}</span>
                    </span>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">Posted to the job's group chat with a push notification. Crew tap ✓ / ✗ in the chat; the PM &amp; dispatcher(s) see everything — no reply needed from them.</p>
              </>
            )}
          </div>

          {/* Message */}
          <div>
            <label className="block text-xs font-semibold text-[#1e3a5f] mb-1.5 uppercase tracking-wide">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className={`${inputCls} h-44 resize-none font-mono text-[13px]`}
              data-testid="dispatch-sms-message"
            />
          </div>

          {/* When */}
          <div>
            <label className="block text-xs font-semibold text-[#1e3a5f] mb-1.5 uppercase tracking-wide">When to send</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('now')}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border transition-colors ${mode === 'now' ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                data-testid="dispatch-sms-mode-now"
              >
                <Send size={15} /> Post now
              </button>
              <button
                type="button"
                onClick={() => setMode('schedule')}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border transition-colors ${mode === 'schedule' ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                data-testid="dispatch-sms-mode-schedule"
              >
                <Clock size={15} /> Schedule
              </button>
            </div>
            {mode === 'schedule' && (
              <div className="mt-3">
                <input
                  type="datetime-local"
                  value={sendAt}
                  onChange={(e) => setSendAt(e.target.value)}
                  className={inputCls}
                  data-testid="dispatch-sms-send-at"
                />
                <p className="text-xs text-slate-500 mt-1">Eastern Time (America/Toronto)</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onSkip} disabled={busy} className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-60" data-testid="dispatch-sms-skip">
            Skip for now
          </button>
          <button
            onClick={submit}
            disabled={busy || (pickRecipients ? checkedIds.length === 0 : recipients.length === 0)}
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60"
            data-testid="dispatch-sms-confirm"
          >
            {busy ? <Loader2 className="animate-spin" size={16} /> : (mode === 'schedule' ? <Clock size={16} /> : <Send size={16} />)}
            {mode === 'schedule' ? 'Schedule dispatch' : 'Post dispatch now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DispatchSmsModal;
